import { Component, EventEmitter, Inject, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { ConstantsService, ErrorService, LayoutService } from '@core/services';
import { ActionItemsTemplateDetailsStoreService } from '@core/services/action-item-template-details-store.service';
import { Observable, Subject } from 'rxjs';
import { UnsavedChangesComponent } from '../unsaved-changes/unsaved-changes.component';
import { EditActionItemsDialog, EditActionItemsDialogForm, PostCaseActionItems, PostTemplateActionItems, PostStageWithActionItems } from '@core/models/action-item-templates/edit-action-items-dialog.model';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { ActionItemsTemplatesService } from '@core/services/action-item-templates.service';
import { indicate, closeDialogOnEscape } from '@shared/helpers';
import { StatusChips, StiiraError } from '@core/models';
import { finalize } from 'rxjs/operators';
import { LeaveAdminService } from '@core/services/leave-admin.service';
import { LeaveAdminStoreService } from '@core/services/leave-admin-store.service';
import { ActionItemsTemplateStage, TemplateActionItem } from '@core/models/action-item-templates/action-item-template-stage.model';
import { position, onDragEnded } from '@shared/helpers/dialog-drag-constraints';
import { PendingActionsAlertComponent } from '../pending-actions-alert/pending-actions-alert.component';

@Component({
    selector: 'app-edit-action-items',
    templateUrl: './edit-action-items.component.html',
    styleUrl: './edit-action-items.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditActionItemsComponent {
  static readonly componentName = 'EditActionItemsComponent';
  
  @Output() isEditing = new EventEmitter<boolean>();

  public form: FormGroup<EditActionItemsDialogForm>;
  public isMobile$: Observable<boolean>;
  public formInitValues: any;
  public isSubmitting$: Subject<boolean> = new Subject();
  public stageActionItemsArray: TemplateActionItem[][] = [];
  public availableActionItemOptions: TemplateActionItem[] = [];
  public templateIsLoading$: Subject<boolean> = new Subject<boolean>();
  public position = position;
  public onDragEnded = onDragEnded;
  public chipStyles: StatusChips;

  private formChangeEmitted: boolean = false;

  get noChanges(): boolean {
    return JSON.stringify(this.stageActionItemsArray) === JSON.stringify(this.formInitValues);
  }

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      dialogOptions: EditActionItemsDialog;
      templateId: number; //nullable
      caseId: number; //nullable
      stages: ActionItemsTemplateStage[];
      sysText: any;
      unsavedChangesSysText: any;
    },
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<EditActionItemsComponent>,
    private actionItemsTemplateService: ActionItemsTemplatesService,
    private leaveAdminService: LeaveAdminService,
    private errorService: ErrorService,
    private actionItemsTemplateStore: ActionItemsTemplateDetailsStoreService,
    private leaveAdminStore: LeaveAdminStoreService,
    private fb: FormBuilder,
    private layoutService: LayoutService,
    private constants: ConstantsService
  ) { 
    this.form = this.fb.group({
      template: [null],
      keepItemsWithActivity: [true]
    })
  }

  ngOnInit(): void {
    closeDialogOnEscape(this.dialogRef, this.isSubmitting$, () => this.cancel());

    this.chipStyles = this.constants.STAT_CHIP_STYLES;
    this.setupLists(this.data.stages);
    this.isMobile$ = this.layoutService.isMobile$();
    this.formInitValues = JSON.parse(JSON.stringify(this.stageActionItemsArray));
  }

  public save(): void {
    if (this.data.caseId != null) {
      if (this.leaveAdminStore.isActionRunning) {
        this.openPendingChangesDialog();
        return;
      }
      const dto: PostCaseActionItems = {
        caseId: this.data.caseId,
        stages: this.buildDtoStages()
      }
      this.leaveAdminService.postCaseActionItems(dto)
        .pipe(indicate(this.isSubmitting$))
        .subscribe((res)=>{
            this.leaveAdminStore.caseDetails = res;
            this.dialogRef.close();
          },(err: StiiraError) => this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors)
        );
    } else {
      const dto: PostTemplateActionItems = {
        templateId: this.data.templateId,
        stages: this.buildDtoStages()
      }
      this.actionItemsTemplateService.postTemplateActionItems(dto)
        .pipe(indicate(this.isSubmitting$))
        .subscribe((res)=>{
            this.actionItemsTemplateStore.templateDetails = res;
            this.dialogRef.close();
          },(err: StiiraError) => this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors)
        );
    }
  }

  public cancel(): void {
    if (this.noChanges) {
      this.dialogRef.close();
    } else {
      this.openUnsavedChangesDialog();
    }
  }

  public dropActionItemList(event: CdkDragDrop<string[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    }
    this.sortActionItems(this.availableActionItemOptions);
    this.checkChanges();
  }

  public dropStageList(event: CdkDragDrop<string[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    }
    this.checkChanges();
  }

  public onTemplateSelect(id: number): void {
    this.form.controls.template.disable();
      this.leaveAdminService.getTemplateActionItems(id)
        .pipe(
          indicate(this.templateIsLoading$),
          finalize(()=>{this.form.controls.template.enable()}))
        .subscribe((res)=>{
          this.handleMergeTemplate(res.stages, this.form.controls.keepItemsWithActivity.value);
        });
  }

  public onRemoveActionItem(stageIndex: number, actionItemIndex: number, stageName: HTMLElement): void {
    transferArrayItem(
        this.stageActionItemsArray[stageIndex],
        this.availableActionItemOptions,
        actionItemIndex,
        this.availableActionItemOptions.length
      );
    this.sortActionItems(this.availableActionItemOptions);
    this.checkChanges();
    stageName.focus();
  }

  // keyboard alternative to dragging an item into a stage; it goes at the end of the stage
  public onAddActionItem(actionItemIndex: number, stageIndex: number, actionItemsHeading: HTMLElement): void {
    transferArrayItem(
      this.availableActionItemOptions,
      this.stageActionItemsArray[stageIndex],
      actionItemIndex,
      this.stageActionItemsArray[stageIndex].length
    );
    this.checkChanges();
    // after the menu has returned focus to its trigger, which leaves with the item
    setTimeout(() => actionItemsHeading.focus());
  }

  // keyboard alternative to dragging within a stage (offset -1 is up, 1 is down)
  public onMoveActionItem(stageIndex: number, actionItemIndex: number, offset: number, button: HTMLElement): void {
    const stageActionItems = this.stageActionItemsArray[stageIndex];
    const newIndex = actionItemIndex + offset;
    if (newIndex < 0 || newIndex >= stageActionItems.length) {
      return;
    }
    moveItemInArray(stageActionItems, actionItemIndex, newIndex);
    this.checkChanges();
    // the list re-inserts both swapped rows, which drops focus
    setTimeout(() => button.focus());
  }

  private openUnsavedChangesDialog(): void {
    const dialogConfig: MatDialogConfig = {
      id: UnsavedChangesComponent.componentName,
      width: '300px',
      data: this.data.unsavedChangesSysText
    };
    
    this.dialog.open(UnsavedChangesComponent, dialogConfig)
      .afterClosed().subscribe((res: boolean) => {
        if (res) {
          this.dialogRef.close();
        }
      });
  }

  private setupLists(actionItemsTemplateStages: ActionItemsTemplateStage[]): void {
    const stages: ActionItemsTemplateStage[] = JSON.parse(JSON.stringify(actionItemsTemplateStages)); //copy w/o ref
    let asgdIds: number[] = [];

    actionItemsTemplateStages.forEach((s) => {
      s.actionItems.forEach((ai) => {
        asgdIds.push(ai.actionId);
      })
    });

    stages.forEach((s) => {
      this.stageActionItemsArray.push(s.actionItems);
    });

    this.populateAvailableActionItems(asgdIds);
  }

  private handleMergeTemplate(
    templateStages: ActionItemsTemplateStage[],
    keepItemsWithActivity: boolean): void {
    if (!keepItemsWithActivity){
      this.stageActionItemsArray = [];
    } else {
      this.stageActionItemsArray.forEach((saia, i) => {
        this.stageActionItemsArray[i] = saia.filter(ai => ai.hasActivity);
      });
    }

    templateStages.forEach((templateStage, stageIndex) => {
      if (!this.stageActionItemsArray[stageIndex]){
        this.stageActionItemsArray.push([]);
      }
      templateStage.actionItems.forEach((templateActionItem, actionItemIndex) => {
        let currentActionItem = this.stageActionItemsArray[stageIndex][actionItemIndex];
        if (currentActionItem?.hasActivity == false){
          currentActionItem = templateActionItem;
        } else if (!currentActionItem) {
          const hasActivity = this.data.stages[stageIndex].actionItems
            .find(ai => templateActionItem.actionId == ai.actionId)?.hasActivity == true;
          templateActionItem.hasActivity = hasActivity;
          this.stageActionItemsArray[stageIndex].push(templateActionItem);
        }
      });
    });

    let asgdIds: number[] = [];
    this.stageActionItemsArray.forEach((s) => {
      s.forEach((ai) => {
        asgdIds.push(ai.actionId);
      })
    });

    this.populateAvailableActionItems(asgdIds);
  }

  private populateAvailableActionItems(allActionItemIds: number[]): void {
    this.availableActionItemOptions = this.data.dialogOptions.actionItemOptions.map(aio => {
      return {
        actionId: aio.actionId,
        caption: aio.caption,
        description: aio.description,
        owner: aio.owner,
        hasActivity: this.data.stages.some(s => {
          return s.actionItems.some(ai => ai.actionId == aio.actionId && ai.hasActivity);
        }),
        notifications: null
      }
    });

    for (let i = this.availableActionItemOptions.length - 1; i >= 0; i--) {
      if (allActionItemIds.includes(this.availableActionItemOptions[i].actionId)) {
        this.availableActionItemOptions.splice(i, 1);
      }
    }
    this.sortActionItems(this.availableActionItemOptions);
  }

  private sortActionItems(actionItems: TemplateActionItem[]): void {
    actionItems.sort((a, b) => {
      const capA = a.caption.toUpperCase();
      const capB = b.caption.toUpperCase();
      return capA < capB ? -1 : capA > capB ? 1 : 0;
    });
  }

  private checkChanges(): void {
    if (!this.formChangeEmitted && !this.noChanges) {
      this.isEditing.emit(true);
      this.formChangeEmitted = true;
    } else if (this.noChanges) {
      this.isEditing.emit(false);
      this.formChangeEmitted = false;
    }
  }

  private buildDtoStages(): PostStageWithActionItems[] {
    let stages: PostStageWithActionItems[] = [];
    this.data.stages.forEach((stage, i) => {
      stages.push({
        stageId: stage.stageId,
        actionItems: this.stageActionItemsArray[i].map((ai) => {
          return ai.actionId
        })
      })
    });
    return stages;
  }

  private openPendingChangesDialog(): void {
    const dialogConfig: MatDialogConfig = {
      id: PendingActionsAlertComponent.componentName,
      width: '300px'
    };

    this.dialog.open(PendingActionsAlertComponent, dialogConfig)
      .beforeClosed()
      .subscribe((res: boolean) => {
        if (res) {
          this.save();
        }
    });
  }
}
